<template>
    <div>
        <title-bar :title-stack="titleStack" />
        <hero-bar>
            Formulare din {{ formsPageYear }} ({{ formsCount }})
            <div slot="right">
                <div v-for="(column, index) in columnsVisible" :key="index" class="control is-inline-block">
                    <b-checkbox v-model="column.display">
                        {{ column.title }}
                    </b-checkbox>
                </div>
            </div>
        </hero-bar>
        <section class="section is-main-section custom-section">
            <b-modal
                full-screen
                v-model="isFormEditorActive"
                has-modal-card
                trap-focus
                :destroy-on-hide="true"
                aria-role="dialog"
                aria-modal
            >
                <template>
                    <form-editor @close="closeModal"></form-editor>
                </template>
            </b-modal>
            <card-component title="Lista formulare" class="has-table has-mobile-sort-spaced" icon="account-multiple">
                <div class="columns column-control is-vcentered">
                    <div class="column is-4">
                        <b-field class="is-fullwidth">
                            <b-input
                                :loading="isLoading"
                                @keypress.native.enter="onSearch"
                                placeholder="Cauta dupa Nume/Prenume/CNP/Cont/Telefon"
                                type="search"
                                v-model.trim="search"
                                icon="magnify"
                                class="is-fullwidth"
                            >
                            </b-input>
                            <p class="control">
                                <b-button @click="onSearch" type="is-primary" label="Cauta" />
                            </p>
                        </b-field>
                    </div>
                    <div class="column is-4"></div>
                </div>

                <b-table
                    :loading="isLoading"
                    :checkable="false"
                    :paginated="true"
                    :total="formsCount"
                    :per-page="formsLimit"
                    :striped="true"
                    :hoverable="true"
                    :data="formsList"
                    detailed
                    detail-key="id"
                    show-detail-icon
                    backend-sorting
                    backend-pagination
                    @page-change="onPageChange"
                    @sort="onSort"
                    aria-next-label="Next page"
                    aria-previous-label="Previous page"
                    aria-page-label="Page"
                    aria-current-label="Current page"
                    :default-sort-direction="defaultSortOrder"
                    :default-sort="[defaultSortField, defaultSortOrder]"
                    :debounce-search="1000"
                >
                    <b-table-column label="ID" field="id" sortable v-slot="props">
                        {{ props.row.id }}
                    </b-table-column>
                    <b-table-column :custom-search="onCustomSearch" label="Nume" field="nume" sortable v-slot="props">
                        {{ props.row.nume }}
                    </b-table-column>
                    <b-table-column label="Prenume" field="prenume" sortable v-slot="props">
                        {{ props.row.prenume }}
                    </b-table-column>
                    <b-table-column label="Judet" field="judet" sortable v-slot="props">
                        {{ props.row.forms_address.judet }}
                    </b-table-column>
                    <b-table-column label="Localitate" field="localitate" sortable v-slot="props">
                        {{ props.row.forms_address.localitate }}
                    </b-table-column>
                    <b-table-column label="CNP" field="cnp" sortable v-slot="props">
                        {{ props.row.cnp }}
                    </b-table-column>
                    <b-table-column label="Cont" field="account.iban" sortable v-slot="props">
                        <b-tooltip :label="props.row.account.name" type="is-dark" position="is-top">
                            {{ props.row.account.iban.slice(props.row.account.iban.length - 4) }}
                        </b-tooltip>
                    </b-table-column>
                    <b-table-column
                        :visible="columnsVisible['telefon'].display"
                        :label="columnsVisible['telefon'].title"
                        centered
                        field="telefon"
                        sortable
                        v-slot="props"
                    >
                        {{ props.row.telefon }}
                    </b-table-column>
                    <b-table-column
                        centered
                        :visible="columnsVisible['email'].display"
                        :label="columnsVisible['email'].title"
                        field="Email"
                        sortable
                        v-slot="props"
                    >
                        {{ props.row.email }}
                    </b-table-column>
                    <b-table-column centered label="Perioada" field="perioada" sortable v-slot="props">
                        {{ props.row.perioada > 1 ? `${props.row.perioada} ani` : '1 an' }}
                    </b-table-column>
                    <b-table-column centered label="An" field="year" sortable v-slot="props">
                        {{ props.row.year }}
                    </b-table-column>
                    <b-table-column centered label="Metoda" field="method" sortable v-slot="props">
                        {{ props.row.method }}
                    </b-table-column>
                    <b-table-column
                        centered
                        :visible="columnsVisible['manual'].display"
                        :label="columnsVisible['manual'].title"
                        field="manual"
                        sortable
                        v-slot="props"
                    >
                        <b-tag :type="+props.row.manual == 1 ? 'is-warning' : 'is-info'">{{
                            +props.row.manual == 1 ? 'Da' : 'Nu'
                        }}</b-tag>
                    </b-table-column>
                    <b-table-column
                        centered
                        sortable
                        :visible="columnsVisible['createdAt'].display"
                        :label="columnsVisible['createdAt'].title"
                        field="createdAt"
                        v-slot="props"
                    >
                        <small class="has-text-grey is-abbr-like">{{
                            moment(props.row.createdAt).format('DD/MM/YYYY h:mm:ss')
                        }}</small>
                    </b-table-column>
                    <b-table-column field="forms_rejected.reason" centered label="Respins" sortable v-slot="props">
                        <template v-if="props.row.forms_rejected != null">
                            <b-tooltip
                                :label="`Formular respins : ${props.row.forms_rejected.reason}`"
                                type="is-dark"
                                position="is-top"
                            >
                                <b-icon
                                    icon="circle"
                                    size="is-small"
                                    :class="{
                                        bullet: true,
                                        'is-clickable': true,
                                        rejected: true,
                                        active: props.row.forms_rejected == null,
                                    }"
                                >
                                </b-icon>
                            </b-tooltip>
                        </template>
                        <template v-else>
                            <b-icon
                                icon="circle"
                                size="is-small"
                                :class="{ bullet: true, rejected: true, active: props.row.forms_rejected == null }"
                            >
                            </b-icon>
                        </template>
                    </b-table-column>
                    <b-table-column field="dublat" centered label="Dublat" v-slot="props">
                        <b-icon
                            icon="circle"
                            size="is-small"
                            :class="{ bullet: true, dublat: true, active: props.row.dublat }"
                        >
                        </b-icon>
                    </b-table-column>
                    <b-table-column custom-key="actions" cell-class="is-actions-cell" v-slot="props">
                        <div class="buttons is-right">
                            <b-tooltip label="Modifica formularul" type="is-dark" position="is-top">
                                <b-button
                                    @click="openFormEditor(props.row.id)"
                                    :disabled="props.row.telefon == null || props.row.telefon === ''"
                                    :loading="isApprovingLoading"
                                    type="is-black"
                                    icon-right="file-edit-outline"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                            <b-tooltip
                                v-if="props.row.status == 0"
                                label="Aproba formularul"
                                type="is-dark"
                                position="is-top"
                            >
                                <b-button
                                    @click="approveForm(props.row.id)"
                                    :loading="isApprovingLoading"
                                    type="is-primary"
                                    icon-right="check-underline"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                            <b-tooltip label="Decarca PDF" type="is-dark" position="is-top">
                                <b-button
                                    @click="downloadFormPDF(props.row.id)"
                                    :disabled="props.row.telefon == null || props.row.telefon === ''"
                                    :loading="isDownloadLoading"
                                    type="is-info"
                                    icon-right="file-pdf-box-outline"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                            <b-tooltip label="Sterge formular" type="is-dark" position="is-left">
                                <b-button
                                    @click="deleteForm(props.row.id)"
                                    :disabled="props.row.telefon == null || props.row.telefon === ''"
                                    :loading="isLoading"
                                    type="is-danger"
                                    icon-right="trash-can"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                        </div>
                    </b-table-column>
                    <template #detail="props">
                        <article>
                            <table class="table is-bordered full-width">
                                <thead>
                                    <tr>
                                        <th>Initiala</th>
                                        <th>Strada</th>
                                        <th>Numar</th>
                                        <th>Bloc</th>
                                        <th>Scara</th>
                                        <th>Etaj</th>
                                        <th>Ap.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{{ props.row.initiala || '-' }}</td>
                                        <td>{{ props.row.forms_address.strada || '-' }}</td>
                                        <td>{{ props.row.forms_address.numar || '-' }}</td>
                                        <td>{{ props.row.forms_address.bloc || '-' }}</td>
                                        <td>{{ props.row.forms_address.scara || '-' }}</td>
                                        <td>{{ props.row.forms_address.etaj || '-' }}</td>
                                        <td>{{ props.row.forms_address.apartament || '-' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                    </template>
                    <section slot="empty" class="section">
                        <div class="content has-text-grey has-text-centered">
                            <template v-if="isLoading">
                                <p>
                                    <b-icon icon="dots-horizontal" size="is-large" />
                                </p>
                                <p>Se incarca...</p>
                            </template>
                            <template v-else>
                                <p>
                                    <b-icon icon="emoticon-sad" size="is-large" />
                                </p>
                                <p>Nu exista date&hellip;</p>
                            </template>
                        </div>
                    </section>
                </b-table>
            </card-component>
        </section>
    </div>
</template>

<script>
import CardComponent from '@/components/CardComponent';
import TitleBar from '@/components/TitleBar';
import HeroBar from '@/components/HeroBar';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import * as toast from '@/utils/toast';
import * as dialogs from '@/utils/dialog';
import debounce from 'lodash/debounce';
import { createBlob } from '@/utils/functions';
const FormEditor = () => import('./components/FormEditor');
export default {
    name: 'Formulare',
    components: {
        CardComponent,
        HeroBar,
        TitleBar,
        FormEditor,
    },
    data: () => ({
        isLoading: false,
        moment,
        columnsVisible: {
            telefon: { title: 'Telefon', display: false },
            email: { title: 'Email', display: false },
            manual: { title: 'Manual', display: false },
            createdAt: { title: 'Data', display: false },
        },
        isApprovingLoading: false,
        isDownloadLoading: false,
        isFormEditorActive: false,
    }),
    computed: {
        ...mapState({
            defaultSortField: (state) => state.admin.formular230.forms.sortField,
            defaultSortOrder: (state) => state.admin.formular230.forms.sortOrder,
        }),
        ...mapGetters({
            currentPage: 'admin/currentPage',
            formsList: 'admin/formsList',
            formsCount: 'admin/formsCount',
            formsLimit: 'admin/formsLimit',
            formsSearch: 'admin/formsSearch',
            formsPageYear: 'admin/formsPageYear',
        }),
        titleStack() {
            return ['Admin', 'Declaratie 230', 'Formulare'];
        },
        search: {
            get() {
                return this.formsSearch;
            },
            set(value) {
                this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'search', value });
            },
        },
    },
    methods: {
        async getForms() {
            this.isLoading = true;
            try {
                await this.$store.dispatch('admin/GetForms230');
            } catch (err) {
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        async deleteForm(id) {
            const confirmation = await dialogs.confirm('Confirmare', `Sterg formularul ${id}`, 'Confirm', 'is-warning');
            if (!confirmation.result) return false;
            this.isLoading = true;
            return this.$store
                .dispatch('admin/DeleteForm230', id)
                .then((res) => {
                    return toast.success('Formularul a fost sters', 5000, 'is-top');
                })
                .catch((err) => toast.error('Eroare la stergerea formularului'))
                .finally(() => (this.isLoading = false));
        },
        async onPageChange(page) {
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'page', value: page });
            return this.getForms();
        },
        async onSort(field, order) {
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'sortField', value: field });
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'sortOrder', value: order });
            return this.getForms();
        },
        onSearch() {},
        async downloadFormPDF(formId) {
            this.isDownloadLoading = true;
            try {
                const pdfData = await this.$store.dispatch('admin/DownloadFormPDF', formId);
                const blobUrl = createBlob(pdfData.mime, pdfData.data);
                const dlnk = document.createElement('a');
                dlnk.href = blobUrl;
                dlnk.setAttribute('target', '_blank');
                dlnk.download = 'formular-' + formId + '.' + pdfData.extension;
                document.body.appendChild(dlnk);
                dlnk.click();
                dlnk.remove();
            } catch (e) {
                console.log(e);
                toast.error('Eroare la descarcarea formularului');
            } finally {
                this.isDownloadLoading = false;
            }
        },
        async approveForm(formid) {
            const confirmation = await dialogs.confirm(
                'Confirmare',
                `Aprob formularul ${formid} ?`,
                'Confirm',
                'is-info'
            );
            if (!confirmation.result) return false;
            this.isApprovingLoading = true;
            try {
                await this.$store.dispatch('admin/ApproveForm', formid);
                toast.success(`Formularul <strong>${formid}</strong> a fost aprobat`);
            } catch (err) {
                console.log(e);
            } finally {
                this.isApprovingLoading = false;
            }
        },
        async onCustomSearch(a, input) {
            console.log(a, input);
        },
        closeModal() {
            this.isFormEditorActive = false;
        },
        openFormEditor(formId) {
            this.$store.commit('admin/SET_CURRENT_FORM_ID', formId);
            return (this.isFormEditorActive = true);
        },
    },
    watch: {
        '$route.params.year'(val) {
            this.$store.commit('admin/SET_FORMULARE230_PAGE_YEAR', +val);
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'manual', value: 0 });
            return this.getForms();
        },
    },
    async created() {
        if (this.$route.params?.year == null) {
            return this.$router.push({ name: 'Formulare230', params: { year: 2025 } });
        }
        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'manual', value: 0 });
        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'status', value: '' });
        this.$store.commit('admin/SET_FORMULARE230_PAGE_YEAR', +this.$route.params.year);
        this.onSearch = debounce(() => {
            return this.getForms();
        }, 300);
        await this.getForms();
    },
};
</script>

<style lang="scss" scoped>
::v-deep .level {
    align-items: baseline;
}

.is-fullwidth {
    width: 100%;
}
</style>
